import type { ReactNode } from "react";
import { AlertCircle, Lock } from "lucide-react";

import { ApiError } from "@/lib/apiClient";

import { BTN } from "./ui";

export function Panel({
  title,
  aside,
  children,
  id,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  /** Anchor for in-page links (the waiting banner links to the Live view). */
  id?: string;
}) {
  return (
    <section
      id={id}
      aria-label={title}
      className="scroll-mt-4 rounded-md border border-paper-line bg-paper-2"
    >
      <header className="flex items-baseline justify-between gap-2 border-b border-paper-line px-4 py-2">
        <h2 className="text-sm font-medium text-ink-1">{title}</h2>
        {aside && <span className="text-[11px] text-ink-3">{aside}</span>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Same wording pattern as PermissionDeniedScreen, sized for a section. */
export function DeniedNotice({ what, roles }: { what: string; roles: string }) {
  return (
    <div className="flex items-start gap-2 text-sm text-ink-2">
      <Lock size={16} className="mt-0.5 shrink-0 text-ink-4" aria-hidden />
      <div>
        <div className="text-ink-1">You don't have access to {what}.</div>
        <div className="mt-0.5 text-xs text-ink-3">
          This needs the {roles} role. Ask your entity admin or a Flow global admin.
        </div>
      </div>
    </div>
  );
}

export function ErrorNotice({
  message,
  error,
  onRetry,
}: {
  message: string;
  error: unknown;
  onRetry?: () => void;
}) {
  const correlationId = error instanceof ApiError ? error.correlationId : null;
  return (
    <div role="alert" className="flex items-start gap-2 text-sm">
      <AlertCircle size={16} className="mt-0.5 shrink-0 text-status-failed" aria-hidden />
      <div>
        <div className="text-ink-1">{message}</div>
        {correlationId && (
          <div className="mt-0.5 font-mono text-[10px] text-ink-3">Reference: {correlationId}</div>
        )}
        {onRetry && (
          <button type="button" onClick={onRetry} className={`mt-2 ${BTN}`}>
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

export function Skeleton({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-sm bg-paper-tint ${className}`} />;
}
